<template>
    <div class="room">
        <div class="roomTitle">
            {{room.title}}

            <span v-if="currentMonth && isClean !== null" :class="{cleaned: isClean}" class="fa fa-info-circle fa-fw pull-right status" v-tooltip.top-center="cleaningStatus"></span>
        </div>

        <div class="days">
            <div class="navigatable" v-tooltip="(matchedReservations[i]) ? matchedReservations[i].name : null" @click="select(i)" v-for="i in daysInMonth" :class="{
                today: currentMonth && i === today.day(),
                reserved: matchedReservations[i] !== null && matchedReservations[i] !== undefined,
                first: isFirst(i, matchedReservations[i]),
                last: isLast(i, matchedReservations[i]), isPaid: (matchedReservations[i] && matchedReservations[i].paid),
                grouped: (matchedReservations[i] && groupedReservations[matchedReservations[i].id])
            }">
                <template v-if="i < 10">0</template>{{i}}
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    $roomTitleWidth: 200px;
    $padding: 10px;

    .room {
        display: flex;
        font-size: 15px;
        border-top: 3px solid #e0e0e0;

        &:last-child {
            border-bottom: 3px solid #e0e0e0;
        }

        .status {
            opacity: 0.5;
            color: #89001c;
            transition: all 0.3s ease;
            margin-right: 10px;

            &.cleaned {
                color: #007000;
                opacity: 1;
            }
        }

        .roomTitle {
            font-size: 10pt;
            min-width: $roomTitleWidth;
            padding: $padding;
            font-weight: 500;
            margin-top: 3px;
        }

        .days {
            display: flex;
            flex: 1 0 auto;

            > * {
                color: #ccc;

                padding: $padding;

                flex-grow: 1;
                flex-shrink: 1;
                flex-basis: 0;
                text-align: center;

                &.today {
                    color: #a6a6a6;
                    border-bottom: 0;
                }

                &.reserved {
                    cursor: pointer;
                    font-weight: 500;

                    color: #000000;
                    background-color: #f0c6cb;

                    margin: 5px 0;
                    padding: 5px 10px;

                    &.first {
                        border-top-left-radius: 40%;
                        border-bottom-left-radius: 40%;

                        margin-left: 5px;
                        padding-left: 5px;
                    }

                    &.last {
                        border-top-right-radius: 40%;
                        border-bottom-right-radius: 40%;

                        margin-right: 5px;
                        padding-right: 5px;
                    }

                    &.isPaid {
                        background-color: #dff0d8;
                    }
                }

                &.navigation-selected-active {
                    position: relative;

                    background-color: transparent;

                    &:after {
                        content: " ";
                        border: 2px solid #ff3d70;
                        -webkit-border-radius: 50%;
                        -moz-border-radius: 50%;
                        border-radius: 50%;

                        position: absolute;
                        left: 0;
                        top: 1px;

                        width: 100%;
                        height: 95%;
                    }
                }

                &.selected {
                    color: #fff;
                    font-weight: 500;
                    background-color: #ff3d70;

                    &.first {
                        border-top-left-radius: 50%;
                        border-bottom-left-radius: 50%;
                    }

                    &.last {
                        border-top-right-radius: 50%;
                        border-bottom-right-radius: 50%;
                    }
                }
            }

            .grouped {
                background-color: #7387de !important;
            }
        }
    }
</style>

<script>
    const moment = require('moment')

    const ReservationService = require('@/services/reservations/ReservationService')
    const CleaningService = require('@/services/cleaning/CleaningService')

    export default {
        data() {
            return {
                start: null,
                end: null,
                today: moment(),
                reservations: [],
                matchedReservations: {},
                hovered: null,
                cleaning: null,
                hasLoadedCleaning: null
            }
        },

        props: {
            room: Object,
            date: Object,
            groupedInvoice: Boolean,
            groupedReservations: Object,
            selected: Boolean
        },

        computed: {
            daysInMonth() {
                if (!this.date) {
                    return 0
                }

                return this.date.daysInMonth()
            },

            currentMonth() {
                return (
                    this.date.month() === this.today.month() &&
                    this.date.year() === this.today.year()
                )
            },

            cleaningStatus() {
                if (!this.hasLoadedCleaning) {
                    return 'Loading...'
                }

                if (!this.cleaning) {
                    return 'Not cleaned today'
                }

                const date = moment(this.cleaning.created.date)
                const today = moment()

                if (date.format('YYYY-MM-DD') !== today.format('YYYY-MM-DD')) {
                    return 'Not cleaned today'
                }

                return 'Cleaned today by ' + this.cleaning.user.name
            },

            isClean() {
                if (!this.hasLoadedCleaning) {
                    return null
                }

                if (!this.cleaning) {
                    return false
                }

                const date = moment(this.cleaning.created.date)
                const today = moment()

                return (date.format('YYYY-MM-DD') === today.format('YYYY-MM-DD'))
            }
        },

        watch: {
            date() {
                this.loadReservations()
            }
        },

        mounted() {
            this.loadReservations()
            this.loadCleanings()

            eventHub.$on('room.cleaned', this.notifyOnCleaning)
            eventHub.$on('reservation.updated', this.reloadReservations)
        },

        destroyed() {
            eventHub.$off('room.cleaned', this.notifyOnCleaning)
            eventHub.$off('reservation.updated', this.reloadReservations)
        },

        methods: {
            select(day) {
                if (this.matchedReservations[day]) {
                    if (this.groupedInvoice) {
                        this.$emit('reservationsGroup', this.matchedReservations[day])

                        return
                    }

                    eventHub.$emit('reservations.overlay', this.matchedReservations[day].id)

                    return
                }
            },

            reloadReservations(payload) {
                if (!payload.room || payload.room !== this.room.id) {
                    return
                }

                this.loadReservations()
            },

            notifyOnCleaning(payload) {
                if (payload.room !== this.room.id) {
                    return
                }

                this.loadCleanings()

                this.$toastr('success', this.room.title + ' has been cleaned', 'Notification')
            },

            /**
             * Is selected
             *
             * @param i
             * @returns {boolean}
             */
            isSelected(i) {
                return (this.start <= i && this.end >= i)
            },

            /**
             * Load reservations
             */
            loadReservations() {
                this.hasLoaded = false
                this.matchedReservations = {}

                const options = {
                    filter_groups: [
                        {
                            filters: [
                                {
                                    key: 'belongsToMonth',
                                    value: this.date.format('YYYY-MM'),
                                    operator: 'eq'
                                },
                            ]
                        },
                        {
                            filters: [
                                {
                                    key: 'room',
                                    value: this.room.id,
                                    operator: 'eq'
                                },
                            ]
                        }
                    ]
                }

                ReservationService.findAll(options, (response) => {
                    const data = response.data

                    this.reservations = data.rows
                    this.hasLoaded = true

                    for(let i = 0; i < this.daysInMonth; i++) {
                        const day = i + 1

                        this.$set(this.matchedReservations, day, this.reservation(day))
                    }

                    this.$emit('reservationsLoaded')
                })
            },

            reservation(i) {
                const date = moment(this.date.format('YYYY-MM') + '-' + i, "YYYY-MM-DD")

                const filterReservations = this.reservations.filter((reservation) => {
                    const arrival = moment(reservation.arrival.date)
                    const departure = moment(reservation.departure.date)

                    return (arrival <= date && departure >= date)
                })

                return (filterReservations.length > 0) ? filterReservations[0] : null
            },

            isFirst(i, reservation) {
                if (!reservation) {
                    return false
                }

                const date = moment(this.date.format('YYYY-MM') + '-' + i, "YYYY-MM-DD")

                return (date.isSame(moment(reservation.arrival.date)))
            },

            isLast(i, reservation) {
                if (!reservation) {
                    return false
                }

                const date = moment(this.date.format('YYYY-MM') + '-' + i, "YYYY-MM-DD")

                return (date.isSame(moment(reservation.departure.date)))
            },

            loadCleanings() {
                const options = {
                    includes: ['user'],

                    limit: 1,

                    sort: [{
                        key: 'created',
                        direction: 'DESC'
                    }],

                    filter_groups: [
                        {
                            filters: [
                                {
                                    key: 'room',
                                    value: this.room.id,
                                    operator: 'eq'
                                }
                            ]
                        }
                    ]
                }

                CleaningService.findAll(options, (response) => {
                    const data = response.data

                    if (data.total) {
                        this.cleaning = data.rows[0]
                    }

                    this.hasLoadedCleaning = true
                })
            }
        }
    }
</script>



// WEBPACK FOOTER //
// Room.vue?1a28e07b